import { components, paths } from "@/api-schema";
import {
  AppRoleAttributeType,
  PathsV1AppRoleSearchPostParametersQueryExtend,
  PathsV1AppRoleSearchPostParametersQueryOrderByDirection,
  PathsV1AppRoleSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { UNLIMITED_PAGE_SIZE } from "@/common/api/_defaults";
import { useAppRoleSearchQuery } from "@/common/api/app-role/use-app-role-search.query";
import { useAttributeSearchQuery } from "@/common/api/attribute/use-attribute-search.query";
import { getAppRoleV1Identifier } from "@/common/helpers/app-role-identifiers";
import { mapAttributeValueToName } from "@/common/helpers/map-attribute-value-to-name";
import { renderAttributeAppRole } from "@/common/helpers/render-attribute-app-role";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { DataTableRow } from "@/components/data-table/types";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { CommandButton } from "@/components/command-palette/command-button";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { RowActionVariant, type RowAction } from "@/components/ui/table-row-actions";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import i18next from "i18next";
import { EditIcon, EyeIcon, PlusCircleIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  GroupDetails,
  PredefinedTableConfig,
  TableHooks,
} from "./_components/base-predefined-table.types";
import { appRoleTableDefaultColumns } from "./app-role-table-columns";

type AppRole = components["schemas"]["AppRole"] & RowWithIdentifier;
type AppRoleSorting = NonNullable<paths["/v1/app-role/search"]["post"]["parameters"]["query"]["orderBy"]>;
type AppRoleQueryParams = paths["/v1/app-role/search"]["post"]["parameters"]["query"];
type AppRoleRequestBody = paths["/v1/app-role/search"]["post"]["requestBody"]["content"]["application/json"];
type AppRoleExtend = NonNullable<paths["/v1/app-role/search"]["post"]["parameters"]["query"]["extend"]>;

export enum AppRoleType {
  Attribute = "attribute",
  Default = "default",
  Protected = "protected",
}

// Extended AppRole type for grouping functionality
type ExtendedAppRole = AppRole & {
  appRoles?: AppRole[];
  attribute?: boolean | components["schemas"]["AppRoleAttribute"];
  type?: AppRoleType;
};

type CustomRowParam = AppRoleTableProps["customRowActions"] extends (row: infer R, ...rest: never[]) => unknown
  ? R
  : { original: ExtendedAppRole };
type CustomRowHelpers = Parameters<NonNullable<AppRoleTableProps["customRowActions"]>>[1];

const DEFAULT_EXTEND: AppRoleExtend = [
  PathsV1AppRoleSearchPostParametersQueryExtend.Attribute,
  PathsV1AppRoleSearchPostParametersQueryExtend.AttributeBranch,
  PathsV1AppRoleSearchPostParametersQueryExtend.AttributeBranch_Address,
  PathsV1AppRoleSearchPostParametersQueryExtend.AttributeCompany,
  PathsV1AppRoleSearchPostParametersQueryExtend.AttributeCompanyGroup,
  PathsV1AppRoleSearchPostParametersQueryExtend.AppRoleIdentifiers,
] as const;

const APP_ROLE_TABLE_CONFIG: PredefinedTableConfig<AppRoleSorting> = {
  defaultPageSize: UNLIMITED_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1AppRoleSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1AppRoleSearchPostParametersQueryOrderByField.Name,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchByName"),
    features: {
      multiSelection: false,
      pagination: false,
      search: true,
      expansion: true,
    },
  } as const,
  defaultRowActions: {
    showEdit: false,
    showView: false,
    showDelete: false,
  } as const,
  sortingMapping: {
    name: ["name"],
  } as const,
} as const;

const APP_ROLE_TABLE_QUERY_HOOKS: DataFetchHooks<AppRole, AppRoleQueryParams, AppRoleRequestBody> = {
  useSearchQuery: useAppRoleSearchQuery,
} as const;

const APP_ROLE_TABLE_HOOKS: TableHooks<AppRole | ExtendedAppRole> = {
  groupByCallback: (rows: (AppRole | ExtendedAppRole)[]): (GroupDetails<AppRole> | ExtendedAppRole)[] => {
    const grouped: GroupDetails<AppRole>[] = [];
    const ungrouped: (AppRole | ExtendedAppRole)[] = [];

    rows.forEach((row: AppRole | ExtendedAppRole) => {
      if (row.name.includes(".")) {
        const [groupName] = row.name.split(".");

        let group: GroupDetails<AppRole> = grouped.find((group) => group.name === groupName);
        if (!group) {
          group = {
            name: groupName,
            data: [],
            indentColumnIndexes: [1],
          };
          grouped.push(group);
        }

        group.data.push(row);
      } else {
        ungrouped.push(row);
      }
    });

    return [...grouped, ...ungrouped];
  },
} as const;

const PROTECTED_APP_ROLES = ["msiam_access", "User"];
const AVAILABLE_ATTRIBUTE_TYPES: AppRoleAttributeType[] = [
  AppRoleAttributeType.Branch,
  AppRoleAttributeType.Company,
  AppRoleAttributeType.CompanyGroup,
];

export interface AppRoleTableProps extends BasePredefinedTableProps<
  AppRole,
  AppRoleSorting,
  AppRoleQueryParams,
  AppRoleRequestBody
> {
  showAttributeButton?: boolean;
  attributeButtonText?: string;
  attributeButtonLink?: string;
  onDeleteAttribute?: (uuid: string) => Promise<void>;
  enableGrouping?: boolean;
  displayMode?: "normal" | "attribute" | "both";
}

const BaseAppRoleTable = createBasePredefinedTable({
  config: APP_ROLE_TABLE_CONFIG,
  queryHooks: APP_ROLE_TABLE_QUERY_HOOKS,
  tableHooks: APP_ROLE_TABLE_HOOKS,
  defaultColumns: [appRoleTableDefaultColumns.columnName],
});

export function AppRoleTable(props: AppRoleTableProps) {
  const { t } = useTranslation("common");
  const {
    name = "app-role-table",
    createButtonText = t("ram:AddAppRole"),
    showCreateButton = false,
    showAttributeButton = false,
    attributeButtonText = t("ram:AddAppRoleAttribute"),
    attributeButtonLink = "/attribute",
    onDeleteAttribute,
    enableGrouping = false,
    displayMode = "both",
    extend = DEFAULT_EXTEND,
    headerComponent,
    customRowActions,
    body,
    autoFetch = true,
    searchPlaceholder = APP_ROLE_TABLE_CONFIG.defaultTableConfig.searchPlaceholder,
    pageSize = APP_ROLE_TABLE_CONFIG.defaultPageSize,
    page = 1,
    query,
    ...restProps
  } = props;

  // Internal state management for grouping mode (similar to base table)
  const [internalPaginationState, setInternalPaginationState] = useState({
    page: query?.page ?? page,
    pageSize: query?.pageSize ?? pageSize,
  });
  const [internalSearchValue, setInternalSearchValue] = useState(query?.search ?? "");
  const [internalSorting, setInternalSorting] = useState<AppRoleSorting | null>(
    (query?.orderBy ?? APP_ROLE_TABLE_CONFIG.defaultSorting) as AppRoleSorting,
  );

  // Determine current state (similar to base table)
  const currentPaginationState = query
    ? { page: query.page ?? page, pageSize: query.pageSize ?? pageSize }
    : internalPaginationState;
  const currentSearchValue = query?.search ?? internalSearchValue;
  const currentSorting = query?.orderBy ?? internalSorting;

  // Fetch AppRoleAttribute records for this application so that assigned attribute
  // types are always visible in the table even when they have
  // no entity instances yet (and therefore no individual AppRole rows).
  const applicationUuid = body?.application?.[0];
  const isAttributeSearchEnabled = (enableGrouping || showAttributeButton) && Boolean(applicationUuid);
  const { data: attributeData } = useAttributeSearchQuery({
    options: {
      body: { application: [applicationUuid!] },
      parameters: { pageSize: UNLIMITED_PAGE_SIZE },
    },
    enabled: isAttributeSearchEnabled,
  });

  // Fetch data manually when grouping is enabled
  const {
    data: rawData,
    isFetching: isLoadingRaw,
    error: errorRaw,
  } = useAppRoleSearchQuery({
    options:
      enableGrouping && autoFetch
        ? {
            parameters: {
              search: currentSearchValue,
              page: currentPaginationState.page,
              pageSize: currentPaginationState.pageSize,
              orderBy: currentSorting ? currentSorting : undefined,
              extend: extend as PathsV1AppRoleSearchPostParametersQueryExtend[],
              ...query,
            },
            body,
          }
        : undefined,
    enabled: enableGrouping && autoFetch,
  });

  // Transform data for grouping when enableGrouping is true
  const transformedData = useMemo(() => {
    if (!enableGrouping || !rawData?.data) {
      return undefined;
    }

    const groupedData: ExtendedAppRole[] = [];
    const attributeGroups: Record<string, ExtendedAppRole> = {};

    const regularAppRoles: ExtendedAppRole[] = [];
    const attributeAppRoles: ExtendedAppRole[] = [];

    for (const appRole of rawData.data as AppRole[]) {
      if (appRole.attribute) {
        attributeAppRoles.push({
          ...appRole,
          type: AppRoleType.Attribute,
        });
      } else if (PROTECTED_APP_ROLES.includes(appRole.name)) {
        regularAppRoles.push({
          ...appRole,
          type: AppRoleType.Protected,
        });
      } else {
        regularAppRoles.push({
          ...appRole,
          type: AppRoleType.Default,
        });
      }
    }

    for (const appRole of attributeAppRoles) {
      if (appRole.attribute) {
        const attributeType = appRole.attribute.type;
        const attributeName = mapAttributeValueToName(attributeType);
        const groupId = `attribute-${attributeType}`;

        if (!attributeGroups[groupId]) {
          attributeGroups[groupId] = {
            uuid: groupId,
            name: attributeName || attributeType,
            createdAt: appRole.createdAt,
            updatedAt: appRole.updatedAt,
            azureObjectUuid: "",
            attribute: appRole.attribute,
            appRoles: [],
            type: AppRoleType.Attribute,
          };
        }

        attributeGroups[groupId].appRoles?.push(appRole);
      }
    }

    if (attributeData?.data) {
      for (const attribute of attributeData.data) {
        const groupId = `attribute-${attribute.type}`;

        if (!attributeGroups[groupId]) {
          attributeGroups[groupId] = {
            uuid: groupId,
            name: mapAttributeValueToName(attribute.type) || attribute.type,
            createdAt: attribute.createdAt,
            updatedAt: attribute.updatedAt,
            azureObjectUuid: "",
            attribute: attribute,
            appRoles: [],
            type: AppRoleType.Attribute,
          };
        }
      }
    }

    groupedData.push(...Object.values(attributeGroups));
    groupedData.push(...regularAppRoles);

    return groupedData;
  }, [enableGrouping, rawData, attributeData]);

  const needsComplexHandling = enableGrouping || showAttributeButton || onDeleteAttribute || displayMode !== "both";
  const areAllAttributeAppRoleTypesAdded = AVAILABLE_ATTRIBUTE_TYPES.every((attributeType) =>
    attributeData?.data?.some((attribute) => attribute.type === attributeType),
  );

  // Custom header component for complex cases
  const customHeaderComponent = useMemo(() => {
    if (!needsComplexHandling) {
      return undefined;
    }

    if (headerComponent !== undefined) {
      return headerComponent;
    }

    if (!showCreateButton && !showAttributeButton) {
      return null;
    }

    return (
      <div className="flex items-center gap-2">
        {showCreateButton && (
          <CommandButton
            commandId={`create-${restProps.createButtonLink || "/create"}`}
            label={createButtonText}
            icon={<PlusCircleIcon />}
            to={restProps.createButtonLink || "/create"}
          />
        )}
        {showAttributeButton && (
          <>
            {areAllAttributeAppRoleTypesAdded ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-not-allowed" tabIndex={0} aria-disabled="true">
                    <Button
                      type="button"
                      variant={ButtonVariant.Outlined}
                      colorVariant={ButtonColorVariant.Secondary}
                      disabled
                    >
                      <PlusCircleIcon /> {attributeButtonText}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{t("ram:AllAttributeAppRoleTypesAlreadyAdded")}</TooltipContent>
              </Tooltip>
            ) : (
              <CommandButton
                commandId={`create-attribute-${attributeButtonLink}`}
                label={attributeButtonText}
                icon={<PlusCircleIcon />}
                to={attributeButtonLink}
                variant={ButtonVariant.Outlined}
                colorVariant={ButtonColorVariant.Secondary}
              />
            )}
          </>
        )}
      </div>
    );
  }, [
    needsComplexHandling,
    headerComponent,
    showCreateButton,
    restProps.createButtonLink,
    createButtonText,
    showAttributeButton,
    areAllAttributeAppRoleTypesAdded,
    attributeButtonLink,
    attributeButtonText,
    t,
  ]);

  // Custom row actions for complex cases
  const enhancedRowActions = useCallback(
    (row: CustomRowParam, helpers: CustomRowHelpers) => {
      if (!needsComplexHandling) {
        return undefined;
      }

      if (customRowActions) {
        return customRowActions(row, helpers);
      }

      const { original } = row as { original: ExtendedAppRole };

      const actions: RowAction[] = [];
      const { attribute, type } = original;

      if (type === AppRoleType.Protected) {
        return [];
      }

      const isV1ManagedAppRole = getAppRoleV1Identifier(original) !== undefined;

      if (restProps.onView && !attribute) {
        actions.push({
          label: t("View"),
          icon: <EyeIcon className="h-4 w-4" />,
          onClick: () => restProps.onView?.(original),
        });
      }

      if (restProps.onEdit && !attribute) {
        actions.push({
          label: t("ram:EditAppRole"),
          icon: <EditIcon className="h-4 w-4" />,
          onClick: () => restProps.onEdit?.(original),
        });
      }

      if (restProps.onDelete && !attribute && !isV1ManagedAppRole) {
        actions.push({
          label: t("ram:DeleteAppRole"),
          icon: <Trash2Icon className="h-4 w-4" />,
          variant: RowActionVariant.Error,
          onClick: () => restProps.onDelete?.(original),
        });
      }

      if (onDeleteAttribute && attribute && enableGrouping) {
        if (typeof attribute === "object" && "uuid" in attribute) {
          actions.push({
            label: t("DeleteAttribute"),
            icon: <Trash2Icon className="h-4 w-4" />,
            variant: RowActionVariant.Error,
            onClick: () => onDeleteAttribute(attribute.uuid),
          });
        }
      }

      return actions;
    },
    [needsComplexHandling, customRowActions, restProps, onDeleteAttribute, enableGrouping, t],
  );

  // Custom sub-component for expansion
  const subComponent = useCallback(
    (props: { row: { original: ExtendedAppRole } }) => {
      if (!needsComplexHandling || !enableGrouping) {
        return undefined;
      }

      const appRoleRow = props.row.original;
      if (!appRoleRow.appRoles || appRoleRow.appRoles.length === 0) {
        return null;
      }

      return (
        <div className="bg-muted/30 p-4">
          <div className="flex flex-wrap gap-2">
            {appRoleRow.appRoles.map((subRole) => (
              <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Secondary} key={subRole.uuid}>
                {renderAttributeAppRole(subRole)}
              </Badge>
            ))}
          </div>
        </div>
      );
    },
    [needsComplexHandling, enableGrouping],
  );

  // Enhanced features for expansion
  const enhancedFeatures = useMemo(() => {
    if (!needsComplexHandling) {
      return undefined;
    }

    return {
      ...APP_ROLE_TABLE_CONFIG.defaultTableConfig.features,
      expansion: enableGrouping,
      ...restProps.features,
    };
  }, [needsComplexHandling, enableGrouping, restProps.features]);

  const handleRowClick = useCallback(
    (item: AppRole, row?: DataTableRow<AppRole>) => {
      if (enableGrouping && row?.getCanExpand()) {
        row.toggleExpanded();
      }

      restProps.onRowClick?.(item, row);
    },
    [enableGrouping, restProps],
  );

  const baseTableProps = {
    name,
    createButtonText,
    showCreateButton,
    autoFetch: enableGrouping ? false : autoFetch,
    body,
    extend,
    searchPlaceholder,
    pageSize,
    page: enableGrouping ? currentPaginationState.page : undefined,
    query: enableGrouping ? undefined : query, // Don't pass query when grouping to avoid conflicts
    data: enableGrouping ? transformedData : undefined,
    isLoading: enableGrouping ? isLoadingRaw : undefined,
    isError: enableGrouping ? !!errorRaw : undefined,
    totalItems: enableGrouping ? rawData?.meta?.total : undefined,
    ...(enableGrouping && {
      onPaginationChange:
        restProps.onPaginationChange ??
        ((newPaginationState) => {
          setInternalPaginationState((prev) => {
            if (prev.page === newPaginationState.page && prev.pageSize === newPaginationState.pageSize) {
              return prev;
            }
            return newPaginationState;
          });
        }),

      onSearchChange:
        restProps.onSearchChange ??
        ((value: string) => {
          setInternalSearchValue((prev) => (prev === value ? prev : value));
        }),

      onSortingChange:
        restProps.onSortingChange ??
        ((newSorting) => {
          const convertedSorting = newSorting
            ? (newSorting.map((sort) => ({
                field: sort.field as AppRoleSorting[number]["field"],
                direction: sort.direction,
              })) as AppRoleSorting)
            : null;

          setInternalSorting((prev) => {
            const prevJson = JSON.stringify(prev ?? null);
            const nextJson = JSON.stringify(convertedSorting ?? null);
            if (prevJson === nextJson) {
              return prev;
            }
            return convertedSorting;
          });
        }),
    }),

    ...restProps,
    ...(needsComplexHandling && {
      onRowClick: handleRowClick,
      headerComponent: customHeaderComponent,
      customRowActions: enhancedRowActions,
      features: enhancedFeatures,
      subComponent: subComponent,
      getRowCanExpand: enableGrouping
        ? (row: ExtendedAppRole) => Boolean(row.appRoles && row.appRoles.length > 0)
        : undefined,
    }),
  };

  return <BaseAppRoleTable {...baseTableProps} />;
}
